import { useEffect, useState } from 'react';
import type { FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

import { LogoMark } from './logo';
import { XIcon } from './icons';
import {
  resetPassword,
  signInWithApple,
  signInWithEmail,
  signInWithGoogle,
  signUpWithEmail,
} from '../hooks/use-auth';

export type AuthMode = 'login' | 'signup' | 'reset';

interface AuthModalProps {
  readonly mode: AuthMode;
  readonly onClose: () => void;
  readonly onSwitchMode: (mode: AuthMode) => void;
  /** Called once a session is established or a confirmation email has been sent. */
  readonly onAuthed?: (info: { readonly needsConfirmation: boolean }) => void;
}

export function AuthModal({ mode, onClose, onSwitchMode, onAuthed }: AuthModalProps) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [info, setInfo] = useState<string | null>(null);

  useEffect(() => {
    setError(null);
    setInfo(null);
  }, [mode]);

  // Close on Escape — keeps the modal feeling lightweight rather than modal-y.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setSubmitting(true);
    try {
      if (mode === 'login') {
        await signInWithEmail(email.trim(), password);
        onAuthed?.({ needsConfirmation: false });
      } else if (mode === 'signup') {
        const result = await signUpWithEmail(email.trim(), password, name.trim() || undefined);
        if (result.needsConfirmation) {
          setInfo(t('web.auth.modal.messages.signupConfirm'));
        }
        onAuthed?.(result);
      } else {
        await resetPassword(email.trim());
        setInfo(t('web.auth.modal.messages.resetSent'));
      }
    } catch (err) {
      setError((err as Error).message ?? t('web.auth.modal.messages.errorFallback'));
    } finally {
      setSubmitting(false);
    }
  };

  const oauth = async (provider: 'google' | 'apple') => {
    setError(null);
    setSubmitting(true);
    try {
      if (provider === 'google') await signInWithGoogle();
      else await signInWithApple();
      // OAuth redirects, so we won't return here in practice.
    } catch (err) {
      setError((err as Error).message ?? t('web.auth.modal.messages.signInFailedFallback'));
      setSubmitting(false);
    }
  };

  const title = t(`web.auth.modal.title.${mode}` as const);
  const sub = t(`web.auth.modal.subtitle.${mode}` as const);

  return (
    <div className="nw-modal-bg" role="dialog" aria-modal="true" onClick={onClose}>
      <form
        className="nw-modal"
        onClick={(e) => e.stopPropagation()}
        onSubmit={submit}
        autoComplete="on"
      >
        <button
          type="button"
          className="nw-modal__close"
          onClick={onClose}
          aria-label={t('web.auth.modal.a11y.close')}
        >
          <XIcon size={14} stroke={2.2} />
        </button>

        <div className="nw-modal__brand">
          <LogoMark size={32} />
          <span className="nw-modal__brand-name">NewsWave</span>
        </div>
        <h2 className="nw-modal__title">{title}</h2>
        <p className="nw-modal__sub">{sub}</p>

        {mode !== 'reset' && (
          <>
            <div className="nw-social-btns">
              <button type="button" className="nw-social-btn" onClick={() => oauth('google')} disabled={submitting}>
                <span style={{ fontWeight: 800 }}>G</span> {t('web.auth.modal.providers.google')}
              </button>
              <button type="button" className="nw-social-btn" onClick={() => oauth('apple')} disabled={submitting}>
                <span style={{ fontWeight: 800 }}></span> {t('web.auth.modal.providers.apple')}
              </button>
            </div>
            <div className="nw-divider-or">{t('web.auth.modal.orWithEmail')}</div>
          </>
        )}

        {mode === 'signup' && (
          <div className="nw-input-group">
            <label htmlFor="auth-name">{t('common.fields.name')}</label>
            <input
              id="auth-name"
              className="nw-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('web.auth.modal.fields.namePlaceholder')}
              autoComplete="name"
            />
          </div>
        )}

        <div className="nw-input-group">
          <label htmlFor="auth-email">{t('common.fields.email')}</label>
          <input
            id="auth-email"
            className="nw-input"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('web.auth.modal.fields.emailPlaceholder')}
            autoComplete="email"
            required
          />
        </div>

        {mode !== 'reset' && (
          <div className="nw-input-group">
            <label htmlFor="auth-pwd">{t('common.fields.password')}</label>
            <input
              id="auth-pwd"
              className="nw-input"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder={t('web.auth.modal.fields.passwordPlaceholder')}
              autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
              minLength={mode === 'signup' ? 8 : undefined}
              required
            />
          </div>
        )}

        {error && <div className="nw-modal__error">{error}</div>}
        {info && (
          <div
            className="nw-modal__error"
            style={{
              color: 'var(--success)',
              background: 'color-mix(in oklab, var(--success), transparent 88%)',
              borderColor: 'color-mix(in oklab, var(--success), transparent 70%)',
            }}
          >
            {info}
          </div>
        )}

        <button
          type="submit"
          className="nw-btn nw-btn--primary nw-btn--lg"
          style={{ marginTop: 8 }}
          disabled={submitting}
        >
          {t(`web.auth.modal.submit.${mode}` as const)}
        </button>

        <div className="nw-modal__switch">
          {mode === 'login' && (
            <>
              {t('web.auth.modal.switch.newToNewsWave')}{' '}
              <button type="button" onClick={() => onSwitchMode('signup')}>
                {t('web.auth.modal.switch.createAccount')}
              </button>
              {' · '}
              <button type="button" onClick={() => onSwitchMode('reset')}>
                {t('web.auth.modal.switch.forgotPassword')}
              </button>
            </>
          )}
          {mode === 'signup' && (
            <>
              {t('web.auth.modal.switch.haveAccount')}{' '}
              <button type="button" onClick={() => onSwitchMode('login')}>
                {t('web.auth.modal.switch.signIn')}
              </button>
            </>
          )}
          {mode === 'reset' && (
            <button type="button" onClick={() => onSwitchMode('login')}>
              {t('web.auth.modal.switch.backToSignIn')}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
